import React from 'react';
import { connect } from 'react-redux';
import { withCookies } from 'react-cookie';
import _ from 'lodash';
import {Button, Input, ListScreen, Select} from '../components';
import {
    fetchAiTransactions,
    cleanAiTransactions,
    setUser,
    changeUserSetting,
} from '../actions';
import {
    __,
    formatAmount,
    formatDate,
    getCurrency,
    getCurrencyPrice,
    isEmptyString,
    isValidZip,
    request,
    toNumber
} from '../functions';
import '../assets/styles/ai-transactions.css';
import {
    AI_ADDON_NAMES,
    AI_FUNCTION_NAMES, MARKET,
} from '../config';
import {Tooltip} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

class AiTransactionsScreen extends ListScreen {
    title = __('AI transakcie');

    state = { ...ListScreen.state, ...{
        lightbox: {
            payment: false,
        },
    }};

    isValidPermission() {
        return this.hasPermission('aifunctions');
    }

    getTableName() {
        return 'ai-transactions';
    }

    getFilter() {
        const { items } = this.props;

        return _.reduce(items.filter, (result, options, key) => {
            switch (key) {
                case 'ai_function_id':
                    return { ...result, ...{ [key]: {
                        type: 'select',
                        name: __('AI funkcia'),
                        value: '',
                        options: _.reduce(options, (result, name, id) => {
                            return { ...result, [id]: _.has(AI_FUNCTION_NAMES, name) ? AI_FUNCTION_NAMES[name] : name };
                        }, {}),
                    }}};

                default:
                    return result;
            }
        }, {})
    }

    getColumns() {
        return [
            <div>{__('Názov')}</div>,
            <div>{__('Info')}</div>,
            <div>{__('Cena')}</div>,
            <div>{__('Vytvorené')}</div>,
            <div></div>,
        ];
    }

    getCells(item) {
        const { items } = this.props;

        let type = __('AI funkcia');
        let info = '-';

        switch (item.request_id) {
            case 'addon_related_products':
                type = AI_ADDON_NAMES.related_products;

                if (_.has(item.tokens, 'items')) {
                    info = `${__('Počet produktov')}: ${item.tokens.items}`;
                }
                break;

            case 'search':
                type = AI_ADDON_NAMES.search;

                if (_.has(item.tokens, 'requests')) {
                    info = `${__('Počet dotazov')}: ${item.tokens.requests}`;
                }
                break;

            case 'chat':
                type = AI_ADDON_NAMES.chat;
                info = __('Odpoveď na otázku');
                break;

            default:
                const functionId = toNumber(item.ai_function_id);
                let functionName = '-';

                if (functionId > 0 && _.has(items.ai_functions, functionId)) {
                    functionName = items.ai_functions[functionId];
                    functionName = _.has(AI_FUNCTION_NAMES, functionName) ? AI_FUNCTION_NAMES[functionName] : functionName;
                }

                info = <Tooltip title={functionName}><span>{_.truncate(functionName, { length: 96 })}</span></Tooltip>;
                break;
        }

        return [
            <div>{type}</div>,
            <div>{info}</div>,
            <div>{formatAmount(getCurrencyPrice(item.price), getCurrency(), 8)}</div>,
            <div>{formatDate(item.created, 'dd.mm.yyyy hh:ii')}</div>,
            <div></div>,
        ];
    }

    getTags() {
        const { items } = this.props;

        return [
            {
                name: `${__('Aktuálny kredit')}: ${formatAmount(getCurrencyPrice(items.credit), getCurrency(), 4)}`,
                color: 'secondary',
                icon: <MonetizationOnIcon />,
            },
        ];
    }

    getEmptyText() {
        return __('Zatiaľ nemáte žiadne transakcie');
    }

    confirmPayment(type) {
        const { items } = this.props;

        let settings = items.settings.invoicing;

        if (!_.has(settings, 'country')) {
            // Nema krajinu
            settings = { ...settings, country: MARKET };
        }

        this.showLightbox('payment', { loading: false, type, settings });
    }

    onChangePayment(key, value) {
        const { lightbox } = this.state;

        this.setState({ lightbox: { ...lightbox, payment: {
            ...lightbox.payment,
            settings: { ...lightbox.payment.settings, [key]: value }
        } } });
    }

    createPayment() {
        const { lightbox } = this.state;

        let error = '';

        if (isEmptyString(lightbox.payment.settings.name)) {
            // Nie je vyplneny nazov
            error = __('Nie je vyplnený názov');
        } else if (isEmptyString(lightbox.payment.settings.address)) {
            // Nie je vyplnena adresa
            error = __('Nie je vyplnená adresa');
        } else if (isEmptyString(lightbox.payment.settings.city)) {
            // Nie je vyplnene mesto
            error = __('Nie je vyplnené mesto');
        } else if (!isValidZip(lightbox.payment.settings.zip)) {
            // Neplatne PSC
            error = __('Neplatné PSČ');
        }

        if (error !== '') {
            this.showSnackbar('error', error);
            return null;
        }

        this.setState({ lightbox: { ...lightbox, payment: {...lightbox.payment, loading: true } } });

        request(`/subscriptions/createAiProforma/${lightbox.payment.type}`, { settings: lightbox.payment.settings }, 'POST').then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                // Nepodarilo sa generovat
                this.showSnackbar('error', __('Nepodarilo sa vygenerovať faktúru'));
                this.setState({ lightbox: { ...lightbox, payment: {...lightbox.payment, loading: false } } });
                return;
            }

            // Stiahneme fakturu
            request(`/subscriptions/pdf/${data.id}`).then(response => {
                const { status, data } = response.data;

                if (status === 'error') {
                    // Nepodarilo sa stiahnut pdf
                    this.showSnackbar('error', __('Nepodarilo sa stiahnuť pdf'));
                    this.setState({ lightbox: { ...lightbox, payment: {...lightbox.payment, loading: false } } });
                    return;
                }

                // Stiahneme subor
                window.location = data.link;

                this.showSnackbar('success', __('Faktúra bola stiahnutá'));

                this.closeLightbox('payment');
            });
        });
    }

    renderAfterHeader() {
        const { items } = this.props;
        const disabled = this.isDemo() || _.isEmpty(items.settings);

        return (
            <div className="ai-transactions-header">
                {_.map(items.ai_packages, price => {
                    return (
                        <div className="ai-transactions-header__package" key={price}>
                            <div className="ai-transactions-header__package__name">{__('AI balík')}</div>
                            <div className="ai-transactions-header__package__price">{formatAmount(getCurrencyPrice(price), getCurrency())}</div>
                            <Button
                                onClick={() => this.confirmPayment(price)}
                                color="primary"
                                disabled={disabled}
                            >{__('Objednať')}</Button>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderScreen() {
        const { lightbox } = this.state;

        return (
            <div>
                {this.renderLightbox(
                    'payment',
                    !_.isEmpty(lightbox.payment) ? `${__('Objednávka')} - ${formatAmount(getCurrencyPrice(lightbox.payment.type), getCurrency())}` : '',
                    !_.isEmpty(lightbox.payment) ? <div className="ai_package-lightbox">
                        <div className="ai_package-lightbox__title">{__('Fakturačné údaje')}</div>
                        <div className="ai_package-lightbox__panels">
                            <div className="ai_package-lightbox__panels__panel">
                                <Input
                                    label={__('Adresa')}
                                    value={lightbox.payment.settings.address}
                                    onChange={value => this.onChangePayment('address', value)}
                                />
                                <Input
                                    label={__('Mesto')}
                                    value={lightbox.payment.settings.city}
                                    onChange={value => this.onChangePayment('city', value)}
                                />
                                <Input
                                    label={__('PSČ')}
                                    value={lightbox.payment.settings.zip}
                                    onChange={value => this.onChangePayment('zip', value)}
                                />
                                <Select
                                    label={__('Krajina')}
                                    value={lightbox.payment.settings.country}
                                    options={{ sk: 'sk', cz: 'cz', pl: 'pl', hu: 'hu' }}
                                    onChange={value => this.onChangePayment('country', value)}
                                    allowEmpty={false}
                                />
                            </div>
                            <div className="ai_package-lightbox__panels__panel">
                                <Input
                                    label={__('Názov')}
                                    value={lightbox.payment.settings.name}
                                    onChange={value => this.onChangePayment('name', value)}
                                />
                                <Input
                                    label={__('IČO')}
                                    value={lightbox.payment.settings.ico}
                                    onChange={value => this.onChangePayment('ico', value)}
                                />
                                <Input
                                    label={__('DIČ')}
                                    value={lightbox.payment.settings.dic}
                                    onChange={value => this.onChangePayment('dic', value)}
                                />
                                {lightbox.payment.settings.country === 'sk' ? <Input
                                    label={__('IČ DPH')}
                                    value={lightbox.payment.settings.ic_dph}
                                    onChange={value => this.onChangePayment('ic_dph', value)}
                                /> : null}
                            </div>
                        </div>
                        <Button
                            onClick={() => this.createPayment()}
                            color="green"
                            loading={lightbox.payment.loading}
                        >{__('Vygenerovať faktúru')}</Button>
                    </div> : null,
                    '',
                    __('Zrušiť'),
                    () => {}
                )}
                {this.renderSnackbar()}
            </div>
        );
    }
}

const stateToProps = ({ ai_transactions, user }) => ({ items: ai_transactions, user });

export default withCookies(connect(stateToProps, {
    fetch: fetchAiTransactions,
    clean: cleanAiTransactions,
    setUser,
    changeUserSetting,
})(AiTransactionsScreen));
